import React, { useState, useEffect } from 'react';
import { useApplication } from '../../context/ApplicationContext';
import { aiService } from '../../services/aiService';
import RatingVisualization from './RatingVisualization';
import EvaluationDisplay from '../common/EvaluationDisplay';
import useAdminMode from '../../hooks/useAdminMode';
import toast from 'react-hot-toast';
import { getSchoolPrompt } from '../../config/schoolPrompts';

// Helper function to count words
const countWords = (text) => {
  if (!text || typeof text !== 'string') return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

const Step3Essays = ({ schoolPrompt: legacySchoolPrompt }) => {
  const {
    essays,
    selectedSchool,
    demographics,
    academics,
    resume,
    goals,
    additional,
    updateEssays,
    setEvaluation,
    setLoading,
    markStepComplete,
    goBack,
    canGoBack,
    resetToSchoolSelection,
    evaluations
  } = useApplication();

  const { isAdminMode, handleTripleClick } = useAdminMode();
  const [showDebugPrompt, setShowDebugPrompt] = useState(false);
  const [lastPromptSent, setLastPromptSent] = useState('');
  const [localEssays, setLocalEssays] = useState(() => {
    // Initialize based on selected school
    if (selectedSchool?.value === 'YALE_SOM') {
      return {
        mainEssay: essays.mainEssay || '',
        mainEssayChoice: essays.mainEssayChoice || '',
        careerInterests: essays.careerInterests || '',
        optionalInfo: essays.optionalInfo || '',
        ...essays
      };
    } else if (selectedSchool?.value === 'INSEAD') {
      return {
        careerGoals: essays.careerGoals || '',
        personalLeadership: essays.personalLeadership || '',
        resilience: essays.resilience || '',
        communityImpact: essays.communityImpact || '',
        ...essays
      };
    } else {
      return {
        careerGoals: essays.careerGoals || '',
        whySchool: essays.whySchool || '',
        leadership: essays.leadership || '',
        personalBackground: essays.personalBackground || '',
        ...essays
      };
    }
  });
  const [evaluation, setLocalEvaluation] = useState(null);
  const [finalEvaluation, setFinalEvaluation] = useState(null);
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [showFinalEvaluation, setShowFinalEvaluation] = useState(false);
  const [finalLoading, setFinalLoading] = useState(false);

  // Calculate overall rating from all steps
  const calculateOverallRating = () => {
    const ratings = [];
    if (evaluations?.step1?.rating) ratings.push(evaluations.step1.rating);
    if (evaluations?.step2?.rating) ratings.push(evaluations.step2.rating);
    if (evaluations?.step3?.rating) ratings.push(evaluations.step3.rating);
    if (evaluation?.rating) ratings.push(evaluation.rating);
    
    if (ratings.length === 0) return 0;
    return Math.round(ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length * 10) / 10;
  };

  // Update essays when school changes
  useEffect(() => {
    if (selectedSchool?.value === 'YALE_SOM') {
      setLocalEssays({
        mainEssay: essays.mainEssay || '',
        mainEssayChoice: essays.mainEssayChoice || '',
        careerInterests: essays.careerInterests || '',
        optionalInfo: essays.optionalInfo || '',
        ...essays
      });
    } else if (selectedSchool?.value === 'INSEAD') {
      setLocalEssays({
        careerGoals: essays.careerGoals || '',
        personalLeadership: essays.personalLeadership || '',
        resilience: essays.resilience || '',
        communityImpact: essays.communityImpact || '',
        ...essays
      });
    } else {
      setLocalEssays({
        careerGoals: essays.careerGoals || '',
        whySchool: essays.whySchool || '',
        leadership: essays.leadership || '',
        personalBackground: essays.personalBackground || '',
        ...essays
      });
    }
  }, [selectedSchool?.value, essays]);

  // Get essay questions based on selected school
  const essayQuestions = () => {
    if (selectedSchool?.value === 'YALE_SOM') {
      return {
        mainEssay: {
          label: "Main Essay (Choose One - 500 words max)",
          type: "choice",
          choices: [
            {
              value: "commitment",
              label: "Option 1: Describe the biggest commitment you have ever made. What is the most significant sacrifice you have made to honor this commitment? Why was it important for you to honor this commitment?"
            },
            {
              value: "community", 
              label: "Option 2: Describe the community that has been most meaningful to you. What is the most valuable thing you have gained from being a part of this community and what is the most important thing you have contributed to this community?"
            },
            {
              value: "challenge",
              label: "Option 3: Describe the most significant challenge you have faced. How have you confronted this challenge and how has it shaped you as a person?"
            }
          ],
          placeholder: "Choose one of the three prompts above and write your response here. Focus on specific examples and concrete actions you took.",
          minWords: 50,
          maxWords: 500
        },
        careerInterests: {
          label: "Career Interests (200 words max)",
          placeholder: "Briefly describe your career interests and how you arrived at them. What have you already done to pursue these interests? What do you need to do going forward?",
          minWords: 20,
          maxWords: 200
        },
        optionalInfo: {
          label: "Optional Information (200 words max)",
          placeholder: "If any aspect of your application requires additional explanation, you can address it here. This section is truly optional – if no aspect of your application requires further explanation, you should leave this section blank.",
          minWords: 0,
          maxWords: 200,
          optional: true
        }
      };
    } else if (selectedSchool?.value === 'INSEAD') {
      return {
        careerGoals: {
          label: "Career Goals and Motivation (300 words max)",
          placeholder: "Discuss your short and long-term career aspirations. What is your target geography, industry, and function? How do you plan to bridge the gap between your current role and future ambitions?",
          minWords: 50,
          maxWords: 300
        },
        personalLeadership: {
          label: "Personal Leadership and Character (500 words max)",
          placeholder: "Give a candid description of yourself as a person and a leader. What personal characteristics do you feel are your strengths and weaknesses? What main factors have influenced your personal development?",
          minWords: 100,
          maxWords: 500
        },
        resilience: {
          label: "Resilience and Growth (400 words max)",
          placeholder: "Describe how you navigated a highly stressful situation. How did you demonstrate self-awareness, adaptability, and emotional intelligence? What did you learn from this experience?",
          minWords: 80,
          maxWords: 400
        },
        communityImpact: {
          label: "Community Impact and Growth (300 words max)",
          placeholder: "Describe your involvement in extracurricular activities. How have these activities enriched you personally and professionally? What impact have you had on your community?",
          minWords: 50,
          maxWords: 300
        }
      };
    } else {
      // Default generic questions for other schools
      return {
        careerGoals: {
          label: "Career Goals & Post-MBA Plans",
          placeholder: "Describe your short-term and long-term career goals. How will an MBA help you achieve these goals? Be specific about your target role, industry, and timeline.\n\nExample areas to cover:\n• Immediate post-MBA goals (2-3 years)\n• Long-term career aspirations (5-10 years)\n• How your background prepares you for these goals\n• Skills/knowledge gaps an MBA will fill\n• Why this career path is realistic given your experience",
          minWords: 100,
          maxWords: 200
        },
        whySchool: {
          label: `Why ${selectedSchool?.label || 'This School'}?`,
          placeholder: `Why is ${selectedSchool?.label || 'this school'} the right fit for your goals and background? What specific aspects of the program appeal to you?\n\nExample areas to cover:\n• Specific courses, professors, or curriculum elements\n• Culture and values alignment\n• Geographic location and career opportunities\n• Alumni network and industry connections\n• Clubs, activities, or experiential learning opportunities\n• How the school's strengths match your career goals`,
          minWords: 80,
          maxWords: 160
        },
        leadership: {
          label: "Leadership Experience",
          placeholder: "Describe a time when you demonstrated leadership. What was the situation, your role, and the outcome?\n\nExample areas to cover:\n• Context and challenge you faced\n• Your specific actions and decision-making process\n• How you influenced or motivated others\n• Obstacles you overcame\n• Results and lessons learned\n• How this experience shaped your leadership style",
          minWords: 80,
          maxWords: 160
        },
        personalBackground: {
          label: "Personal Background & Unique Perspective",
          placeholder: "Tell us about your background, interests, and what unique perspective you would bring to the MBA class.\n\nExample areas to cover:\n• Personal background and formative experiences\n• Interests and activities outside of work\n• Challenges you've overcome\n• Diverse perspectives you bring\n• How you contribute to communities\n• What makes you unique among MBA candidates",
          minWords: 80,
          maxWords: 160
        }
      };
    }
  };

  const handleEssayChange = (essayType, value) => {
    setLocalEssays(prev => ({
      ...prev,
      [essayType]: value
    }));
  };

  const validateEssays = () => {
    const errors = [];
    
    Object.entries(essayQuestions()).forEach(([key, question]) => {
      const essay = localEssays[key] || '';
      
      // Skip validation for optional essays that are empty
      if (question.optional && essay.trim().length === 0) {
        return;
      }
      
      const wordCount = countWords(essay);
      
      if (wordCount < question.minWords) {
        errors.push(`${question.label} must be at least ${question.minWords} words`);
      }
      
      if (wordCount > question.maxWords) {
        errors.push(`${question.label} must be no more than ${question.maxWords} words`);
      }
      
      // For choice-based questions, check if a choice is selected
      if (question.type === 'choice' && essay.trim().length > 0) {
        const choiceKey = `${key}Choice`;
        if (!localEssays[choiceKey]) {
          errors.push(`Please select one of the ${question.label} options`);
        }
      }
    });

    // Enhanced goal coherence validation
    const hasResumeContent = resume?.content?.length > 200;
    const careerGoalsEssay = localEssays.careerGoals || '';
    if (hasResumeContent && careerGoalsEssay.length > 200) {
      // This validation will be done by AI during evaluation
      // but we can add basic checks here if needed
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateEssays();
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    try {
      setLoading(true);
      
      // Update application state
      updateEssays(localEssays);

      // Prepare candidate data for evaluation
      const candidateData = {
        selectedSchool: selectedSchool?.value,
        demographics: demographics,
        academics: academics,
        resume: resume,
        essays: localEssays,
        goals: goals || {},
        additional: additional || {}
      };
      
      // Get previous evaluations for context
      const step1Evaluation = evaluations?.step1;
      const step2Evaluation = evaluations?.step2;
      
      const previousContext = [
        step1Evaluation ? `STEP 1 EVALUATION:\n${step1Evaluation.evaluation}\nStep 1 Rating: ${step1Evaluation.rating}/5` : '',
        step2Evaluation ? `\nSTEP 2 EVALUATION:\n${step2Evaluation.evaluation}\nStep 2 Rating: ${step2Evaluation.rating}/5` : ''
      ].filter(Boolean).join('\n');
      
      // Use Step 3 prompt to evaluate essays only
      const schoolPrompt = getSchoolPrompt(selectedSchool?.value, 3);
      
      const formatEssays = () => {
        if (selectedSchool?.value === 'YALE_SOM') {
          const mainEssayChoice = localEssays.mainEssayChoice || 'Not selected';
          const mainEssayTitle = essayQuestions().mainEssay.choices.find(opt => opt.value === mainEssayChoice)?.label || 'Unknown choice';
          
          return `YALE SOM ESSAYS:
1. Main Essay (${mainEssayTitle}):
${localEssays.mainEssay || 'Not provided'}

2. Career Interests:
${localEssays.careerInterests || 'Not provided'}

3. Optional Information:
${localEssays.optionalInfo || 'Not provided'}`;
        } else if (selectedSchool?.value === 'INSEAD') {
          return `INSEAD ESSAYS:
1. Career Goals and Motivation:
${localEssays.careerGoals || 'Not provided'}

2. Personal Leadership and Character:
${localEssays.personalLeadership || 'Not provided'}

3. Resilience and Growth:
${localEssays.resilience || 'Not provided'}

4. Community Impact and Growth:
${localEssays.communityImpact || 'Not provided'}`;
        } else {
          return `ESSAYS:
1. Career Goals: ${localEssays.careerGoals || 'Not provided'}

2. Why ${selectedSchool?.label}: ${localEssays.whySchool || 'Not provided'}

3. Leadership Experience: ${localEssays.leadership || 'Not provided'}

4. Personal Background: ${localEssays.personalBackground || 'Not provided'}`;
        }
      };

      const prompt = `${schoolPrompt}

COMPLETE CANDIDATE PROFILE:
${JSON.stringify(candidateData, null, 2)}

${formatEssays()}

COMPREHENSIVE EVALUATION HISTORY:

${previousContext}

FINAL COMMITTEE INSTRUCTIONS:
This is the FINAL comprehensive evaluation. After reviewing all components (demographics, academics, work experience, and essays), provide a FINAL ADMISSION DECISION. Include:
1. Overall assessment synthesis
2. Key strengths and concerns
3. Final recommendation (ACCEPT/WAITLIST/REJECT)
4. Reasoning for the decision
5. Final overall rating (1-5 scale)

Write this as the committee chair delivering the final decision.`;

      setLastPromptSent(prompt);

      const aiResponse = await aiService.evaluateCandidate(prompt);
      
      if (aiResponse.success) {
        // Process the evaluation to fix formatting
        const processedEvaluation = aiResponse.evaluation
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*]+)\*/g, '<em>$1</em>');
        
        const newEvaluation = {
          step: 3,
          stepName: 'Essays & Goals',
          evaluation: processedEvaluation,
          rating: aiResponse.rating,
          timestamp: new Date().toISOString(),
          isFinal: false
        };
        
        setEvaluation(newEvaluation);
        setLocalEvaluation(newEvaluation);
        setShowEvaluation(true);
        markStepComplete(3);
      } else {
        toast.error('Failed to get evaluation. Please try again.');
      }
    } catch (error) {
      console.error('Error evaluating essays:', error);
      toast.error('Error evaluating essays. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalEvaluation = async () => {
    try {
      setFinalLoading(true);
      
      // Prepare candidate data for final evaluation
      const candidateData = {
        selectedSchool: selectedSchool?.value,
        demographics: demographics,
        academics: academics,
        resume: resume,
        essays: localEssays,
        goals: goals || {},
        additional: additional || {}
      };
      
      // Get all previous evaluations for context
      const step1Evaluation = evaluations?.step1;
      const step2Evaluation = evaluations?.step2;
      const step3Evaluation = evaluations?.step3 || evaluation;
      
      const previousContext = [
        step1Evaluation ? `STEP 1 EVALUATION:\n${step1Evaluation.evaluation}\nStep 1 Rating: ${step1Evaluation.rating}/5` : '',
        step2Evaluation ? `\nSTEP 2 EVALUATION:\n${step2Evaluation.evaluation}\nStep 2 Rating: ${step2Evaluation.rating}/5` : '',
        step3Evaluation ? `\nSTEP 3 EVALUATION:\n${step3Evaluation.evaluation}\nStep 3 Rating: ${step3Evaluation.rating}/5` : ''
      ].filter(Boolean).join('\n');
      
      // Use Step 4 (final) prompt for comprehensive evaluation
      const schoolPrompt = getSchoolPrompt(selectedSchool?.value, 4);
      
      const formatEssays = () => {
        if (selectedSchool?.value === 'YALE_SOM') {
          const mainEssayChoice = localEssays.mainEssayChoice || 'Not selected';
          const mainEssayTitle = essayQuestions().mainEssay.choices.find(opt => opt.value === mainEssayChoice)?.label || 'Unknown choice';
          
          return `YALE SOM ESSAYS:
1. Main Essay (${mainEssayTitle}):
${localEssays.mainEssay || 'Not provided'}

2. Career Interests (300 words max):
${localEssays.careerInterests || 'Not provided'}

3. Optional Information (200 words max):
${localEssays.optionalInfo || 'Not provided'}`;
        } else {
          // INSEAD essays
          return `INSEAD ESSAYS:
1. Job Essays - Career Summary (500 words):
${localEssays.careerSummary || 'Not provided'}

2. Job Essays - Career Goals & INSEAD Fit (300 words):
${localEssays.careerGoals || 'Not provided'}

3. Motivation Essays - Self-Description & Leadership (500 words):
${localEssays.leadership || 'Not provided'}

4. Motivation Essays - Achievement & Failure (400 words):
${localEssays.achievementFailure || 'Not provided'}

5. Motivation Essays - Extracurriculars (300 words):
${localEssays.extracurriculars || 'Not provided'}`;
        }
      };

      const prompt = `${schoolPrompt}

CANDIDATE INFORMATION:
${JSON.stringify(candidateData, null, 2)}

ESSAYS:
${formatEssays()}

PREVIOUS EVALUATIONS:
${previousContext}

Write this as the committee chair delivering the final admission decision with probabilities and key reasoning.`;

      setLastPromptSent(prompt);

      const aiResponse = await aiService.evaluateCandidate(prompt);
      
      if (aiResponse.success) {
        // Process the evaluation to fix formatting
        const processedEvaluation = aiResponse.evaluation
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*]+)\*/g, '<em>$1</em>');
        
        const newFinalEvaluation = {
          step: 4,
          stepName: 'Final Admission Decision',
          evaluation: processedEvaluation,
          rating: aiResponse.rating,
          timestamp: new Date().toISOString(),
          isFinal: true
        };
        
        setFinalEvaluation(newFinalEvaluation);
        setShowFinalEvaluation(true);
        
        if (aiResponse.provider === 'demo') {
          toast.success('Demo final evaluation completed! Add OpenAI API key for real AI insights.');
        } else {
          toast.success('Final evaluation completed!');
        }
      } else {
        toast.error('Failed to get final evaluation. Please try again.');
      }
    } catch (error) {
      console.error('Error getting final evaluation:', error);
      toast.error('Error getting final evaluation. Please try again.');
    } finally {
      setFinalLoading(false);
    }
  };

  const currentEvaluation = evaluations?.step3 || evaluation;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 
            className="text-2xl font-bold text-gray-800"
            onClick={handleTripleClick}
          >
            Step 3: Essays & Goals
          </h2>
          <div className="flex gap-2">
            {canGoBack && (
              <button
                onClick={goBack}
                className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Back
              </button>
            )}
            <button
              onClick={resetToSchoolSelection}
              className="px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-2">
              {selectedSchool?.value === 'YALE_SOM' ? 'Yale SOM Essay Instructions' : 
               selectedSchool?.value === 'INSEAD' ? 'INSEAD Essay Instructions' : 
               'Essay Guidelines'}
            </h4>
            <div className="text-sm text-blue-800 space-y-1">
              {selectedSchool?.value === 'YALE_SOM' ? (
                <>
                  <p>• Choose ONE of the three main essay prompts below</p>
                  <p>• Focus on what matters most to you personally</p>
                  <p>• Use specific examples and concrete actions</p>
                  <p>• Be authentic and genuine in your response</p>
                  <p>• Keep your main essay under 500 words</p>
                </>
              ) : selectedSchool?.value === 'INSEAD' ? (
                <>
                  <p>• Answer all four essay questions</p>
                  <p>• Focus on leadership, resilience, and global mindset</p>
                  <p>• Be candid about strengths and weaknesses</p>
                  <p>• Show cultural awareness and adaptability</p>
                  <p>• Demonstrate impact in your community</p>
                </>
              ) : (
                <>
                  <p>• Answer each question directly and specifically</p>
                  <p>• Ensure career goals are realistic given your background</p>
                  <p>• Show personality and interests beyond work</p>
                  <p>• Demonstrate genuine knowledge of {selectedSchool?.label}</p>
                  <p>• Use clear, professional English with proper grammar</p>
                </>
              )}
            </div>
          </div>

          {/* Debug Mode - Show AI Prompt */}
          {isAdminMode && (
            <div className="mb-6">
              <button
                onClick={() => setShowDebugPrompt(!showDebugPrompt)}
                className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors text-sm"
              >
                🔍 {showDebugPrompt ? 'Hide' : 'Show'} AI Prompt
              </button>
              
              {showDebugPrompt && lastPromptSent && (
                <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Prompt Sent to AI:</h4>
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-x-auto max-h-96">
                    {lastPromptSent}
                  </pre>
                </div>
              )}
            </div>
          )}

          <div className="space-y-6">
            {Object.entries(essayQuestions()).map(([key, question]) => (
              <div key={key}>
                <label className="block text-lg font-medium text-gray-700 mb-3">
                  {question.label}
                </label>
                
                {question.type === 'choice' && question.choices ? (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      {question.choices.map((option) => (
                        <div key={option.value} className="flex items-start space-x-3">
                          <input
                            type="radio"
                            id={`${key}-${option.value}`}
                            name={`${key}-choice`}
                            value={option.value}
                            checked={localEssays[`${key}Choice`] === option.value}
                            onChange={(e) => handleEssayChange(`${key}Choice`, e.target.value)}
                            className="mt-1 text-blue-600"
                          />
                          <label htmlFor={`${key}-${option.value}`} className="text-sm text-gray-700 leading-relaxed">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                    
                    <div className="relative">
                      <textarea
                        value={localEssays[key] || ''}
                        onChange={(e) => handleEssayChange(key, e.target.value)}
                        placeholder={question.placeholder}
                        className="w-full h-48 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm"
                        style={{ minHeight: '200px' }}
                      />
                      <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                        {countWords(localEssays[key] || '')} / {question.maxWords} words
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <textarea
                      value={localEssays[key] || ''}
                      onChange={(e) => handleEssayChange(key, e.target.value)}
                      placeholder={question.placeholder}
                      className="w-full h-48 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm"
                      style={{ minHeight: '200px' }}
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                      {countWords(localEssays[key] || '')} / {question.maxWords} words
                    </div>
                  </div>
                )}
                
                {localEssays[key] && (
                  <div className="mt-2 text-sm">
                    {!question.optional && (countWords(localEssays[key]) < question.minWords ? (
                      <p className="text-amber-600">
                        ⚠️ Add {question.minWords - countWords(localEssays[key])} more words
                      </p>
                    ) : countWords(localEssays[key]) > question.maxWords ? (
                      <p className="text-red-600">
                        ⚠️ Reduce by {countWords(localEssays[key]) - question.maxWords} words
                      </p>
                    ) : (
                      <p className="text-green-600">
                        ✅ Good length
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="flex justify-between items-center pt-6">
              <div className="text-sm text-gray-600">
                {(() => {
                  const allRequiredFilled = Object.entries(essayQuestions()).every(([key, question]) => {
                    const essay = localEssays[key] || '';
                    if (question.optional && essay.trim().length === 0) return true;
                    
                    // Check if essay meets minimum length
                    if (countWords(essay) < question.minWords) return false;
                    
                    // For choice-based questions, also check if a choice is selected
                    if (question.type === 'choice' && essay.trim().length > 0) {
                      const choiceKey = `${key}Choice`;
                      if (!localEssays[choiceKey]) return false;
                    }
                    
                    return true;
                  });
                  return allRequiredFilled ? (
                    <span className="text-green-600">✅ Essays ready for final evaluation</span>
                  ) : (
                    <span className="text-amber-600">⚠️ Complete all required essays and selections for evaluation</span>
                  );
                })()}
              </div>
              
              <button
                type="submit"
                disabled={(() => {
                  const allRequiredFilled = Object.entries(essayQuestions()).every(([key, question]) => {
                    const essay = localEssays[key] || '';
                    if (question.optional && essay.trim().length === 0) return true;
                    
                    // Check if essay meets minimum length
                    if (countWords(essay) < question.minWords) return false;
                    
                    // For choice-based questions, also check if a choice is selected
                    if (question.type === 'choice' && essay.trim().length > 0) {
                      const choiceKey = `${key}Choice`;
                      if (!localEssays[choiceKey]) return false;
                    }
                    
                    return true;
                  });
                  return !allRequiredFilled;
                })()}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                Get Final Evaluation & Decision
              </button>
            </div>
          </div>
        </form>

        {/* Essays Evaluation Results */}
        {showEvaluation && currentEvaluation && (
          <div className="mt-8">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Essays & Goals Evaluation
              </h3>
              <div className="mb-4">
                <RatingVisualization rating={currentEvaluation?.rating} />
              </div>
            </div>
            
            <EvaluationDisplay 
              evaluation={currentEvaluation?.evaluation}
              rating={currentEvaluation?.rating}
            />

            {/* Final Evaluation Button */}
            {!showFinalEvaluation && (
              <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl text-center">
                <h4 className="text-lg font-semibold text-gray-800 mb-3">
                  Ready for Final Admission Decision
                </h4>
                <p className="text-gray-600 mb-4">
                  Get your comprehensive evaluation with admission probabilities and final recommendation.
                </p>
                <button
                  onClick={handleFinalEvaluation}
                  disabled={finalLoading}
                  className="px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold text-lg shadow-lg"
                >
                  {finalLoading ? 'Getting Final Evaluation...' : 'Get Final Evaluation & Decision'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Final Evaluation Results */}
        {showFinalEvaluation && finalEvaluation && (
          <div className="mt-8">
            {/* Overall Results Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8 shadow-sm">
              <h3 className="text-2xl font-bold text-blue-900 mb-6">
                {selectedSchool?.label} MBA Application Summary
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
                <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-3xl font-bold text-blue-700 mb-1">{calculateOverallRating().toFixed(1)}/5.0</div>
                  <div className="text-sm font-medium text-blue-600">Overall Rating</div>
                </div>
                <div className="text-center bg-white rounded-lg p-4 shadow-sm">
                  <div className="text-xl font-semibold text-gray-700 mb-1">
                    {evaluations?.step1?.rating || 'N/A'} | {evaluations?.step2?.rating || 'N/A'} | {currentEvaluation?.rating || 'N/A'}
                  </div>
                  <div className="text-sm font-medium text-gray-600">Step Ratings</div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Final Admission Committee Decision
              </h3>
              <div className="mb-4">
                <RatingVisualization rating={finalEvaluation?.rating} />
              </div>
            </div>
            
            <EvaluationDisplay 
              evaluation={finalEvaluation?.evaluation}
              rating={finalEvaluation?.rating}
            />

            <div className="mt-6 flex justify-between">
              <button
                onClick={goBack}
                disabled={!canGoBack()}
                className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                ← Back to Work Experience
              </button>
              <button
                onClick={resetToSchoolSelection}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Evaluate Another School →
              </button>
            </div>
          </div>
        )}

        {/* Navigation when no evaluation shown yet */}
        {!showEvaluation && (
          <div className="mt-6 flex justify-between">
            <button
              onClick={goBack}
              disabled={!canGoBack()}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              ← Back to Work Experience
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step3Essays; 